import { useEffect, useRef, useState } from 'react'
import { Button, HStack, Text, VStack } from '@chakra-ui/react'
import { useValidateUserStep } from 'context'
import { VALIDATE_CODE_SCHEMA, ValidateUserPinType } from 'context/ValidateUser'
import { Formik } from 'formik'

import { PinInputControl, SubmitButton } from 'components/InputCustom'
import { customToast } from 'components/ToastCustom'

type ValidateCodeType = Pick<ValidateUserPinType, 'validationCode'>

export const StepOne = () => {
  const [resendForwardEmail, setResendForwardEmail] = useState(false)
  const [validateAccount, setValidateAccount] = useState(false)
  const [loadingForwardSms, setLoadingForwardSms] = useState(false)
  const [timer, setTimer] = useState(60)
  const intervalRef = useRef<NodeJS.Timer>()

  const { forwardSms, activateUser, nextStep, clearError, validateUserState: {
      loading,
      error,
      state: {
        isLogin,
        userValidation: { email, password, phoneNumber  },
      },
    },
  } = useValidateUserStep()

  const initialValues: ValidateCodeType = {
    validationCode: ''
  }

  const handleOnSubmit = async ({ validationCode }: ValidateCodeType) => {
    setValidateAccount(!validateAccount)

    const data: ValidateUserPinType = {
      email,
      validationCode,
    }

    const res = await activateUser(data)

    res && nextStep()
    setValidateAccount(!validateAccount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }

  useEffect(() => {
    if (resendForwardEmail && timer > 0) {
      intervalRef.current && clearInterval(intervalRef.current)
      intervalRef.current = setInterval(() => setTimer(prevStep => prevStep - 1), 1000)
    } else {
      intervalRef.current && clearInterval(intervalRef.current)
      setResendForwardEmail(false)
      setTimer(60)
    }

    return () => intervalRef.current && clearInterval(intervalRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer, resendForwardEmail])

  const resendSms = async () => {
    setLoadingForwardSms(true)

    const res = await forwardSms({ email, password, phoneNumber })

    res && customToast.showToast({
      description: 'El código de activación se ha enviado a tu teléfono',
      status: 'success',
    })
    setLoadingForwardSms(false)
    setResendForwardEmail(true)
  }

  useEffect(() => {
    error && customToast.showToast({ description: error })
    clearError()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validationSchema={VALIDATE_CODE_SCHEMA}>
      <VStack w={'full'} maxW={'330px'} spacing={'32px'}>
        <VStack>
            <Text variant={'LG_SB'} textAlign={'center'}>Ingresa código de validación</Text>
            <Text textAlign={'center'}>
              Te hemos enviado el código de validación por SMS a tu número de teléfono
              <Text as={'span'} variant={'MD_SB'}> {phoneNumber.replace('+569', '')}</Text>
            </Text>
          </VStack>
        <HStack>
          <PinInputControl
            pinAmount={4}
            pinInputProps={{ errorBorderColor: 'red.500', mask: false, isDisabled: loading, type: 'alphanumeric' }}
            name='validationCode'
            placeholder='Ingresar Código'
            label='' />
        </HStack>
        <SubmitButton
          w={'full'}
          isLoading={loading && !loadingForwardSms}
          loadingText='Validando...'>
          Validar
        </SubmitButton>
        <Button
          w={'full'}
          variant={'secondary'}
          onClick={resendSms}
          isLoading={loading && loadingForwardSms}
          disabled={loading || loadingForwardSms || resendForwardEmail && timer > 0}
          loadingText="enviando ...">
          {resendForwardEmail && timer > 0 ? `Reenviar en ${timer}` : 'Reenviar SMS'}
        </Button>
      </VStack>
    </Formik>
  )
}
